import { LitElement, html, css } from "lit";
import { customElement } from "lit/decorators.js";

import * as store from "../state/store.js";
import { SignalWatcher } from "../state/signal-watcher.js";
import {
  dismissAllToasts,
  dismissToast,
  toggleToastStack,
} from "../ops/ui.js";

// Persistent bottom-right notification queue. Two visual modes:
//
//   Collapsed (default): a compact circular badge showing the count of
//     queued notifications. Hovering reveals a small × in the corner that
//     dismisses the entire queue without opening it. Clicking the badge
//     itself opens the expanded list. Renders nothing when the queue is
//     empty — the badge only exists when there's something to acknowledge.
//
//   Expanded: up to 6 entries laid out in a proper column. If the queue is
//     longer, the column scrolls. Each entry has its own close button and
//     an HH:MM:SS timestamp.
//
// Newest-first ordering everywhere: store.toasts() is already sorted so
// index 0 is the freshest entry.
@customElement("dxf-toast")
export class DxfToast extends SignalWatcher(LitElement) {
  static override styles = css`
    :host {
      position: absolute;
      /* 16px from the right; bottom offset clears the ~24px status-bar
         strip with a modest ~8px gap above it — closer than the previous
         44px inset while still staying visibly detached from the strip.
         Adds the safe-area inset on top so it doesn't tuck under the iOS
         home indicator or Android nav bar. */
      right: max(16px, var(--safe-right, 0px));
      bottom: calc(36px + var(--safe-bottom, 0px));
      z-index: 3000;
      font-family: var(--font-body);
      display: block;
      pointer-events: none;
    }

    /* ---- collapsed: circular badge ---- */
    /* Split visual: .circle is the pulsing accent disc (scaled by the
       keyframe animation), and the .count sits on top of it as a
       stationary layer so the number doesn't jitter with the pulse. */
    .badge {
      position: relative;
      pointer-events: auto;
      width: 44px;
      height: 44px;
      color: #fff;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 15px;
      font-weight: 700;
      font-variant-numeric: tabular-nums;
    }
    .circle {
      position: absolute;
      inset: 0;
      border-radius: 50%;
      background: var(--accent);
      box-shadow: 0 6px 18px rgba(0, 0, 0, 0.35);
      transform-origin: center center;
      animation: badge-pulse 1.6s ease-in-out infinite;
    }
    .count {
      position: relative;
      z-index: 1;
      pointer-events: none;
    }
    .badge:hover .circle {
      animation: none;
      transform: scale(1.08);
      box-shadow: 0 10px 24px rgba(0, 0, 0, 0.45);
      transition: transform 0.12s ease, box-shadow 0.12s ease;
    }
    @keyframes badge-pulse {
      0%, 100% {
        transform: scale(1);
        box-shadow: 0 6px 18px rgba(0, 0, 0, 0.35);
      }
      50% {
        transform: scale(1.12);
        box-shadow: 0 10px 26px rgba(0, 0, 0, 0.45);
      }
    }
    /* The corner × only appears on hover — a quick "ack all" that never
       eats a real click. Sits above the badge with pointer-events auto so
       it takes the click even though the badge itself is clickable too. */
    .badge .dismiss-all {
      position: absolute;
      top: -6px;
      right: -6px;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: var(--bg-tertiary, #222);
      color: #fff;
      border: 2px solid var(--canvas-bg, #0d1013);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      opacity: 0;
      transform: scale(0.7);
      transition: opacity 0.12s ease, transform 0.12s ease;
      font-size: 12px;
      line-height: 1;
      padding: 0;
      font-family: inherit;
    }
    .badge:hover .dismiss-all {
      opacity: 1;
      transform: scale(1);
    }
    .badge .dismiss-all:hover {
      background: #b23a3a;
    }

    /* ---- expanded: proper list ---- */
    .expanded {
      pointer-events: auto;
      width: 340px;
      background: var(--overlay-bg, rgba(20, 24, 28, 0.96));
      border: 1px solid var(--border-color);
      border-radius: 10px;
      padding: 8px;
      box-shadow: 0 12px 32px var(--shadow, rgba(0, 0, 0, 0.4));
      backdrop-filter: blur(10px);
      max-height: calc(6 * 84px + 48px);
      display: flex;
      flex-direction: column;
    }
    .expanded .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 4px 8px 8px;
      font-size: 11px;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--text-secondary);
    }
    .expanded .header .actions {
      display: flex;
      gap: 4px;
    }
    .expanded .header .action {
      background: transparent;
      border: none;
      color: var(--text-secondary);
      cursor: pointer;
      font-family: inherit;
      font-size: 11px;
      letter-spacing: 0.04em;
      padding: 2px 6px;
      border-radius: 3px;
    }
    .expanded .header .action:hover {
      background: var(--bg-hover);
      color: var(--text-primary);
    }
    .expanded .list {
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      gap: 6px;
      padding: 2px;
    }
    .expanded .toast {
      background: var(--bg-secondary, var(--bg-hover));
      color: var(--text-primary);
      border: 1px solid var(--border-color);
      border-radius: 6px;
      padding: 10px 12px 10px 14px;
      display: flex;
      align-items: flex-start;
      gap: 10px;
    }
    .expanded .toast .body {
      flex: 1 1 auto;
      display: flex;
      flex-direction: column;
      gap: 4px;
      min-width: 0;
    }
    .expanded .toast .msg {
      font-size: 13px;
      line-height: 1.4;
      overflow-wrap: break-word;
    }
    .expanded .toast .ts {
      font-size: 10.5px;
      color: var(--text-secondary);
      letter-spacing: 0.03em;
      font-variant-numeric: tabular-nums;
    }
    .expanded .toast .close {
      background: transparent;
      border: none;
      color: var(--text-secondary);
      cursor: pointer;
      width: 22px;
      height: 22px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 15px;
      line-height: 1;
      padding: 0;
      flex-shrink: 0;
      transition: background 0.12s, color 0.12s;
    }
    .expanded .toast .close:hover {
      background: var(--bg-hover);
      color: var(--text-primary);
    }

    /* Mobile: the expanded stack is capped at the viewport minus safe
       margins, so a 4-toast burst on a 375px screen doesn't spill under
       the sheet handles. Collapsed badge stays 44px — it's already the
       right size for touch. */
    @media (max-width: 720px) {
      .expanded {
        width: calc(100vw - 32px);
        max-width: 340px;
      }
    }
  `;

  override render() {
    const list = store.toasts();
    const expanded = store.toastsExpanded();
    // No entries → nothing drawn. This is why the circle only appears when
    // there's actually something to acknowledge.
    if (list.length === 0) return html``;

    if (expanded) {
      return html`
        <div class="expanded">
          <div class="header">
            <span>${list.length} notification${list.length === 1 ? "" : "s"}</span>
            <div class="actions">
              <button class="action" @click=${toggleToastStack}>Collapse</button>
              <button class="action" @click=${dismissAllToasts}>Clear all</button>
            </div>
          </div>
          <div class="list">
            ${list.map(
              (t) => html`
                <div class="toast">
                  <div class="body">
                    <div class="msg">${t.message}</div>
                    <div class="ts">${formatTimestamp(t.timestamp)}</div>
                  </div>
                  <button
                    class="close"
                    aria-label="Dismiss"
                    title="Dismiss"
                    @click=${() => dismissToast(t.id)}
                  >×</button>
                </div>
              `
            )}
          </div>
        </div>
      `;
    }

    // Collapsed: circle badge with a scale-based pulse. Click opens the
    // stack; hover reveals the ack-all ×. stopPropagation on the × so it
    // doesn't also fire the badge's click.
    return html`
      <div
        class="badge"
        title="${list.length} notification${list.length === 1 ? "" : "s"} — click to open"
        @click=${toggleToastStack}
      >
        <div class="circle"></div>
        <span class="count">${list.length}</span>
        <button
          class="dismiss-all"
          aria-label="Dismiss all"
          title="Dismiss all"
          @click=${(e: Event) => {
            e.stopPropagation();
            dismissAllToasts();
          }}
        >×</button>
      </div>
    `;
  }
}

function formatTimestamp(ms: number): string {
  const d = new Date(ms);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

declare global {
  interface HTMLElementTagNameMap {
    "dxf-toast": DxfToast;
  }
}
